import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Plus, Trash2 } from "lucide-react";
import { INSURANCE_OPTIONS } from "@/utils/afsol";
import { EXTRA_PARTY_OPTIONS, extraLabel } from "./constants";

const PartyBlock = ({ form, group, partyKey, label, showInsurance, updateParty, loadingMap, onPersonLookup, updateField }) => {
  const party = form[group][partyKey];
  const lk = `${group}-${partyKey}`;
  const isCoOwner = partyKey === "coOwner" || partyKey === "keeper";
  return (
    <div className="space-y-2 bg-slate-50 rounded-lg p-3 border" data-testid={`wizard-party-${group}-${partyKey}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{label}</span>
        {party.name && <Badge variant="outline" className="text-[10px]">{party.name}</Badge>}
      </div>
      {isCoOwner && (
        <div className="flex gap-1" data-testid={`wizard-role-toggle-${group}`}>
          {[
            { value: "medeigandi", label: "Meðeigandi" },
            { value: "umradamadur", label: "Umráðamaður" },
          ].map((r) => (
            <button
              key={r.value}
              onClick={() => updateParty(group, partyKey, "roleMode", r.value)}
              className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${
                party.roleMode === r.value
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-300 text-slate-600 hover:border-blue-400"
              }`}
              data-testid={`wizard-role-${group}-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={party.kennitala || ""}
          onChange={(e) => updateParty(group, partyKey, "kennitala", e.target.value.replace(/\D/g, "").slice(0, 10))}
          placeholder="Kennitala (10 stafir)"
          className="h-8 text-sm font-mono flex-1"
          data-testid={`wizard-kt-${group}-${partyKey}`}
        />
        <Button
          onClick={() => onPersonLookup(group, partyKey)}
          disabled={loadingMap[lk]}
          size="sm" variant="outline" className="h-8 px-2"
          data-testid={`wizard-lookup-${group}-${partyKey}`}
        >
          {loadingMap[lk] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {party.name && (
        <div className="text-xs text-slate-600 space-y-0.5">
          <div>{party.address}{party.postalCode ? `, ${party.postalCode}` : ""} {party.city}</div>
          {party.phone && <div>S: {party.phone}</div>}
        </div>
      )}
      {showInsurance && (
        <div>
          <Label className="text-[10px] text-slate-500">Tryggingar</Label>
          <Select
            value={form.insurance[group === "seller" ? "sellerInsurer" : "buyerInsurer"]}
            onValueChange={(v) => updateField("insurance", group === "seller" ? "sellerInsurer" : "buyerInsurer", v)}
          >
            <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`wizard-insurance-${group}`}>
              <SelectValue placeholder="Veldu tryggingafélag" />
            </SelectTrigger>
            <SelectContent>
              {INSURANCE_OPTIONS.filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

const WizardStepParties = ({
  form, updateField, updateParty, loadingMap, onPersonLookup,
  sellerHasCoOwner, setSellerHasCoOwner, sellerExtraType, setSellerExtraType,
  buyerHasCoOwner, setBuyerHasCoOwner, buyerExtraType, setBuyerExtraType,
}) => {
  const partyBlockProps = { form, updateParty, loadingMap, onPersonLookup, updateField };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wizard-step-parties">
      {/* Seljandi dálkur */}
      <div className="space-y-2">
        <PartyBlock {...partyBlockProps} group="seller" partyKey="owner" label="Seljandi" showInsurance />

        {!sellerHasCoOwner ? (
          <Select value="" onValueChange={(v) => { setSellerExtraType(v); setSellerHasCoOwner(true); if (v === "medeigandi" || v === "umradamadur") updateParty("seller", "coOwner", "roleMode", v); }}>
            <SelectTrigger className="h-7 text-xs border-dashed text-slate-400" data-testid="wizard-seller-extra-select">
              <div className="flex items-center gap-1"><Plus className="h-3 w-3" />Bæta við aðila...</div>
            </SelectTrigger>
            <SelectContent>
              {EXTRA_PARTY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <Badge className="bg-blue-100 text-blue-800 text-[10px]">{extraLabel(sellerExtraType)}</Badge>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                onClick={() => { setSellerExtraType(""); setSellerHasCoOwner(false); }}
                data-testid="wizard-seller-extra-remove">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <PartyBlock {...partyBlockProps} group="seller" partyKey="coOwner"
              label={
                sellerExtraType === "medeigandi" ? "Meðeigandi seljanda" :
                sellerExtraType === "umradamadur" ? "Umráðamaður seljanda" :
                sellerExtraType === "fh" ? "Fyrir Hönd Félags (F.H.) — Seljandi" :
                "Eftir Umboð (E.U.) — Seljandi"
              }
              showInsurance={false} />
          </div>
        )}
      </div>

      {/* Kaupandi dálkur */}
      <div className="space-y-2">
        <PartyBlock {...partyBlockProps} group="buyer" partyKey="owner" label="Kaupandi" showInsurance />

        {!buyerHasCoOwner ? (
          <Select value="" onValueChange={(v) => { setBuyerExtraType(v); setBuyerHasCoOwner(true); if (v === "medeigandi" || v === "umradamadur") updateParty("buyer", "coOwner", "roleMode", v); }}>
            <SelectTrigger className="h-7 text-xs border-dashed text-slate-400" data-testid="wizard-buyer-extra-select">
              <div className="flex items-center gap-1"><Plus className="h-3 w-3" />Bæta við aðila...</div>
            </SelectTrigger>
            <SelectContent>
              {EXTRA_PARTY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <Badge className="bg-green-100 text-green-800 text-[10px]">{extraLabel(buyerExtraType)}</Badge>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                onClick={() => { setBuyerExtraType(""); setBuyerHasCoOwner(false); }}
                data-testid="wizard-buyer-extra-remove">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <PartyBlock {...partyBlockProps} group="buyer" partyKey="coOwner"
              label={
                buyerExtraType === "medeigandi" ? "Meðeigandi kaupanda" :
                buyerExtraType === "umradamadur" ? "Umráðamaður kaupanda" :
                buyerExtraType === "fh" ? "Fyrir Hönd Félags (F.H.) — Kaupandi" :
                "Eftir Umboð (E.U.) — Kaupandi"
              }
              showInsurance={false} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WizardStepParties;
